import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { decode } from "src/utils/flexiblePolyline";

import {
  getBoundingBox,
  getRandomValueToForceRerender,
  ICoordinates,
  IRoutePoint,
  ISandboxMarker
} from "src/components/Map";

import { RioMap } from "@components";
import { ApplicationLayout } from "@rio-cloud/rio-uikit";
import Notification from "@rio-cloud/rio-uikit/Notification";
import { RootState, setIdRouteSelected, useAppSelector } from "@store";
import { CreateAreaReworkPayload } from "@types";
import { formatCategory, formatMetersMaskInNumber, formatPolygon } from "@utils";
import { AxiosError } from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { convertSecondsToHours, convertTimeStringToSeconds } from "src/components/RegisterRoute";
import UpdateSidebarRoute from "src/components/UpdateSidebarRoute";
import { fetchDetail, updateRoute, updateRouteForTrip } from "src/services/routes";
import {
  defineAccountId,
  defineAreasRework,
  defineAvoidAreas,
  defineAvoidToll,
  defineDestination,
  defineDurationWithStops,
  defineOrigin,
  defineRouteCost,
  defineRouteDeviation,
  defineRouteName,
  defineRoutes,
  defineStops,
  defineVehicleInfo,
  defineVehicleInfoDefault,
} from "src/store/routes/RoutesSlice";
import { IResponseFetchDetail, ISendRouteParams, Route } from "src/types/routes";
import { calculateOffsetCoordinates, cleanNumeric } from "../../components/Dynamics/DynamicContentArea";
import { MapDrawingProvider } from "../../providers/MapDrawingContext";
import { getAllAreas, updateAreaRework } from "../../services/areas";
import { getByIdRouteCost, updateRouteCost } from "../../services/costSimulator";
import { ReworkAreasResponse } from "../../types/areas";
import { CreateRouteCostPayload } from "../../types/costSimulator";
import { useAreaRoute } from "@hooks";

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RoutesUpdateScreen = () => {
  const profile = useAppSelector((state) => state.login.userProfile);
  const initialAreasRef = useRef<CreateAreaReworkPayload[]>([]);
  const dispatch = useDispatch();
  const [position, setPosition] = useState<ICoordinates | undefined>({
    lat: -23.5475,
    lng: -46.63611,
  });
  const [boundingBox, setBoundingBox] = useState<
    { top: number; left: number; right: number; bottom: number } | undefined
  >();
  const [segments, setSegments] = useState<IRoutePoint[]>([]);
  const [markers, setMarkers] = useState<ISandboxMarker[]>([]);
  const hasFetchedAreas = useRef(false);
  const [search] = useSearchParams();

  const { routeCost: routeCostStore, areasRework } = useAppSelector((state) => state.routes);
  const durationWithStops = useSelector((state: RootState) => state.routes.durationWithStops);

  const [isCustom, setIsCustom] = useState(false);

  const getRouteIdAndTokenFromURL = () => {
    const params = new URLSearchParams(search);
    const routeId = params.get("id");
    if (!routeId) {
      Notification.error("ID da rota ou token ausente na URL.");
      throw new Error("ID ou token ausente na URL.");
    }
    return { routeId };
  };

  useEffect(() => {
    dispatch(defineOrigin(null));
    dispatch(defineDestination(null));
    dispatch(defineRoutes([]));
    dispatch(defineStops([]));
    dispatch(defineRouteCost({} as CreateRouteCostPayload));
    dispatch(defineRouteName(""));
    dispatch(defineAccountId(""));
    dispatch(defineAvoidToll(false));

    return () => {
      dispatch(defineStops([]));
      dispatch(defineOrigin(null));
      dispatch(defineDestination(null));
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(defineRouteDeviation(0));
    };
  }, []);

  const fetchAndApplyLinkedAreas = async (routeId: string) => {
    if (hasFetchedAreas.current) return;

    try {
      const firstPageResponse: ReworkAreasResponse = await getAllAreas("", 1, 10000, "name,asc", routeId);
      let allAreas: CreateAreaReworkPayload[] = firstPageResponse.items || [];
      const fetchPagePromises: Promise<ReworkAreasResponse>[] = [];
      if (fetchPagePromises.length > 0) {
        const otherPagesResponses = await Promise.all(fetchPagePromises);
        otherPagesResponses.forEach((pageResp, idx) => {
          allAreas = allAreas.concat(pageResp.items || []);
        });
      }

      const avoidAreas = allAreas.filter((a) => a.interestType === "AVOID" && a.selected === true);
      const interestAreas = allAreas.filter((a) => a.interestType === "INTEREST" && a.selected === true);
      dispatch(defineAreasRework([...avoidAreas, ...interestAreas]));

      const formatted = avoidAreas
        .filter((area) => area.selected === true)
        .map((area) => {
          const isCircle = area.circleRadius !== null && area.circleRadius !== undefined;
          if (isCircle) {
            const center = area.points[0];
            const radius = area.circleRadius!;
            const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
            return `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
          }
          return formatPolygon(area.points);
        })
        .join("|");

      dispatch(defineAvoidAreas({ formatted }));

      hasFetchedAreas.current = true;
    } catch (err) {
      console.error("Erro ao buscar áreas vinculadas à rota:", err);
    }
  };

  const fetchRouteDetails = async () => {
    try {
      const { routeId } = getRouteIdAndTokenFromURL();
      const response = await fetchDetail(routeId);
      let responseCost;

      try {
        responseCost = await getByIdRouteCost(routeId);
      } catch (error) {
        console.error("Não foi possível carregar os detalhes do custo da rota.");
      }

      populateMap(response, responseCost);
    } catch (error) {
      console.error(error);
      Notification.error("Não foi possível carregar os detalhes da rota.");
    }
  };

  const handleRoutes = (routes: Route[], via: any[]) => {
    if (routes.length) {
      let ArrayOfShape: any[] = [];
      const route = routes?.[0];
      const data = route?.sections || [];

      const getPolylineArray = data.map((item) => {
        return {
          shape: decode(item.polyline),
        };
      });

      const markers = data.reduce((acum: ISandboxMarker[], curr, index, arr) => {
        if (index === 0) {
          const markerOrigin: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["start"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.departure.place.location.lat,
              lng: curr.departure.place.location.lng,
            },
          };
          acum.push(markerOrigin);
        }
        if (index === arr.length - 1) {
          const markerDestination: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["finish"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerDestination);
        } else {
          const markerStopover: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: [curr.transport.mode === "ferry" ? "rioglyph rioglyph-ship" : "rioglyph rioglyph-pushpin"],
              name: via?.[index]?.position?.label || "",
              markerColor: curr.transport.mode === "ferry" ? "bg-map-marker-asset" : "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerStopover);
        }

        return acum;
      }, []);

      getPolylineArray.forEach((item) => {
        ArrayOfShape.push(item.shape.polyline);
      });
      const concatArrays = ArrayOfShape.flat();
      const getPoints = concatArrays.map((value) => {
        return {
          lat: value[0].toString(),
          lng: value[1].toString(),
        };
      });
      setSegments(getPoints as IRoutePoint[]);
      setMarkers(markers);
      if (getPoints?.length > 0) {
        setPosition(undefined);
        setBoundingBox(getBoundingBox(getPoints));
      } else if (markers?.length > 0) {
        setPosition(markers[0].position);
      }

      const totalRouteDurationSeconds = data.reduce((acc: number, curr) => {
        return acc + (curr.summary?.duration || 0);
      }, 0);

      const totalStopsSeconds = via.reduce((acc: number, stop: any) => {
        return acc + convertTimeStringToSeconds(stop.stayTime);
      }, 0);

      const totalDurationSeconds = totalRouteDurationSeconds + totalStopsSeconds;

      const finalDuration = convertSecondsToHours(totalDurationSeconds);

      dispatch(defineDurationWithStops(finalDuration));
    } else {
      setMarkers([]);
      setSegments([]);
    }
  };

  const parseStayTime = (timeString: string): string => {
    const baseDate = new Date("2000-01-01T00:00:00");
    const targetDate = new Date(timeString);

    const timeDifferenceMs = targetDate.getTime() - baseDate.getTime();

    const hours = Math.floor(timeDifferenceMs / 3600000);
    const minutes = Math.floor((timeDifferenceMs % 3600000) / 60000);

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const populateMap = ({ data }: IResponseFetchDetail, costResponse?: CreateRouteCostPayload) => {
    dispatch(
      defineOrigin({
        id: data.result.originRoute.id,
        label: data.result.originRoute.address.label,
        lng: data.result.originRoute.long,
        lat: data.result.originRoute.lat,
        originChecklistId: data.result.originRoute?.checklist?.id,
      })
    );
    dispatch(
      defineDestination({
        id: data.result.destinyRoute.id,
        label: data.result.destinyRoute.address.label,
        lng: data.result.destinyRoute.long,
        lat: data.result.destinyRoute.lat,
        destinyChecklistId: data.result.destinyRoute?.checklist?.id,
      })
    );
    dispatch(defineRoutes(data.result.responseHere.routes));
    dispatch(defineAvoidToll(data.result.roadParameters?.avoidToll ?? false));
    const stops = data.result.stops.map((stop) => {
      return {
        id: stop.id,
        category: stop.stop.category,
        order: stop.stopQueueOrder,
        stayTime: parseStayTime(stop.stayTime as string),
        position: {
          lat: stop.stop.position.lat,
          lng: stop?.stop?.position?.long || stop?.stop?.position?.lng,
          label: stop.stop.position.address.label,
        },
        checkpointId: stop.checkpointId?.id ?? null,
      };
    });

    dispatch(defineRouteDeviation(data.result.rangeToleranceLimit ?? 0));
    dispatch(defineStops(stops));
    dispatch(defineRouteName(data.result.routeName));
    dispatch(defineRouteCost(costResponse ?? ({} as CreateRouteCostPayload)));
    dispatch(defineAccountId(data.result.accountId));

    dispatch(
      defineVehicleInfo({
        grossWeight: (data.result.vehicleVocationalInfo.maxWeight / 1000).toFixed(2)?.toString().replace(/\./g, ","),
        height: (data.result.vehicleVocationalInfo?.height / 100)?.toString().replace(/\./g, ","),
        lenght: (data.result.vehicleVocationalInfo?.comTotal / 100)?.toString().replace(/\./g, ","),
        width: (data.result.vehicleVocationalInfo?.width / 100)?.toString().replace(/\./g, ","),
        type: data.result.vehicleVocationalInfo.vehicleType,
        maxAxleLoad: data.result.vehicleVocationalInfo.maxWeightAxle?.toString().replace(/\./g, ","),
        numberOfAxles: data.result.vehicleVocationalInfo.numberAxle?.toString().replace(/\./g, ","),
        shippedHazardousGoods: data.result.vehicleVocationalInfo.dangerClassification,
        trailerAxles: data.result.vehicleVocationalInfo.trailerAxle?.toString().replace(/\./g, ","),
        pollutantClass: data.result.vehicleVocationalInfo.pollutantClass?.toString(),
        maxLoadWeight: data.result.vehicleVocationalInfo?.maxLoadWeight || 0,
        weightType: data.result.vehicleVocationalInfo.weightType,
      })
    );

    handleRoutes(data.result.responseHere.routes, stops);
  };

  const { saveAreaNewRoute } = useAreaRoute();

  const sendRoute = async ({
    origin,
    destination,
    stops,
    value,
    routeCost,
    accountId,
    routeDeviation,
    avoidToll,
    areas,
    vehicle,
    areasSelectedIds,
    distance,
  }: ISendRouteParams) => {

    const { routeId } = getRouteIdAndTokenFromURL();
    const params = new URLSearchParams(search);
    const body = {
      id: routeId,
      accountId,
      routeName: value,
      originRoute: {
        id: origin.id,
        address: {
          label: origin.label,
        },
        addressStop: {
          label: origin.label,
        },
        lat: origin.lat,
        lng: origin.lng,
      },
      destinyRoute: {
        id: destination.id,
        address: {
          label: destination.label,
        },
        addressStop: {
          label: destination.label,
        },
        lat: destination.lat,
        lng: destination.lng,
      },
      originChecklistId: origin.originChecklistId,
      destinyChecklistId: destination.destinyChecklistId,
      rangeToleranceLimit: routeDeviation ?? 0,
      stops: stops.map((stop, index) => ({
        id: stop.id,
        name: `Parada ${index + 1}`,
        category: stop.category,
        rangeLimitMeters: "0",
        position: {
          lat: stop.position?.lat,
          lng: stop.position?.lng,
          addressStop: {
            label: stop?.position?.label || "",
          },
        },
        stayTime: `${stop.stayTime}:00`,
        note: formatCategory(stop.category),
        type: "STOPOVER",
        order: stop.order,
        checkpointId: stop.checkpointId ?? null,
      })),
      roadParameters: {
        trafficConditions: "false",
        avoidToll,
        avoidRoad: false,
        ignoreTrafficRestrictions: false,
      },
      vehicleVocationalInfo: {
        height: formatMetersMaskInNumber(vehicle?.height) || 0,
        width: formatMetersMaskInNumber(vehicle?.width) || 0,
        comTotal: formatMetersMaskInNumber(vehicle?.lenght) || 0,
        grossWeight: formatMetersMaskInNumber(vehicle?.grossWeight, "T") || 0,
        maxAxleLoad: parseFloat(vehicle.maxAxleLoad),
        numberOfAxles: parseInt(vehicle.numberOfAxles),
        trailerAxles: parseInt(vehicle.trailerAxles),
        type: vehicle?.type,
        shippedHazardousGoods: vehicle?.shippedHazardousGoods,
        pollutantClass: vehicle?.pollutantClass,
        maxLoadWeight: vehicle.maxLoadWeight,
        weightType: vehicle.weightType,
      },
      costs: {
        tollValue: "0",
        operativeCosts: "0",
        fuelAverageCosts: "0",
        averageConsume: "0",
        totalCosts: "0.00",
      },
      responsibleId: profile?.userId,
      status: true,
      linkedVehicles: [],
      responseHere: { routes: [] },
      duration: durationWithStops,
      distance: distance,
    };

    try {
      const response =
        params.get("trip")?.toString() === "true" || isCustom
          ? await updateRouteForTrip({
            ...body,
            routeName: body.routeName + " - PERSONALIZADO",
          })
          : await updateRoute(body);

      if (response.status === "success" && routeCost.totalCost > 0 && routeCostStore?.id) {
        const updatedRouteCost = { ...routeCost, route: response.data.result, id: routeCostStore.id };
        await updateRouteCost(updatedRouteCost);
      }

      if (response.status === "success") {
        await saveAreaNewRoute(response.data.result, areasRework)
      }

      Notification.info(response.data.message);
      dispatch(defineVehicleInfoDefault());
      dispatch(defineAvoidAreas({ formatted: undefined }));
      dispatch(defineAreasRework([]));
      setTimeout(() => {
        if (params.get("trip")?.toString() === "true" || isCustom) {
          params.set("custom", "true");
          dispatch(setIdRouteSelected(response.data.result));
          navigate("/routing/trips/routes?" + params.toString());
        } else {
          navigate("/routing/routes");
        }
      }, 1000);
    } catch (error) {
      type TError = { message: string };
      const message =
        typeof error === "string"
          ? error
          : (error as AxiosError<TError>)?.response?.data?.message || "Erro desconhecido";
      Notification.error(message);
      return;
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const loadAll = async () => {
      const { routeId } = getRouteIdAndTokenFromURL();
      await fetchRouteDetails();
      await fetchAndApplyLinkedAreas(routeId);
    };

    loadAll();
  }, []);

  const params = new URLSearchParams(search);

  return (
    <div className="width-100pct height-100pct">
      <MapDrawingProvider>
        <UpdateSidebarRoute
          handleRoutes={handleRoutes}
          sendRoute={sendRoute}
          trip={!!params.get("trip")}
          isCustom={isCustom}
          handleCustom={(value) => setIsCustom(value)}
          content={0}
        />
        <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
          <RioMap
            position={position}
            boundingBox={boundingBox}
            apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
            segments={segments.length ? [{ points: segments, alternative: false }] : []}
            markers={markers}
            useClusters={true}
          />
        </ApplicationLayout.Body>
      </MapDrawingProvider>
    </div>
  );
};

export default RoutesUpdateScreen;
